import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { type ColumnType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectSearchInput } from '../advanced-select-common/components/AdvancedSelectSearchInput.tsx';
import { AdvancedSelectTableToolbar } from '../advanced-select-common/components/AdvancedSelectTableToolbar.tsx';
import { type IWidgetAdvancedOptionValue } from '../advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import { getAdvancedTableDataColumns } from '../advanced-select-common/form/getAdvancedTableDataColumns.ts';
import { getAdvancedSelectColumnRenderByDatatype } from '../advanced-select-common/form/getRenderByDatatype.tsx';
import { useAdvancedSelectSearch } from '../advanced-select-common/hooks/useAdvancedSelectSearch.ts';
import { type IRuntimeWidgetAdvancedOption } from '../advanced-select-common/IAdvancedSelectWidget.ts';
import {
  ADVANCED_SELECT_TABLE_SCROLLABLE_HEIGHT,
  sortColumnValues,
} from '../advanced-select-common/table/tableUtils.ts';
import { getUniqueSelectedOptionValue } from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { type IWidgetParsedProps } from '../common/util/getWidgetProps.ts';
import { type IAdvancedSingleSelectWidget } from './IAdvancedSingleSelectWidget.ts';
import { useAdvancedSingleSelectWidgetOptions } from './useAdvancedSingleSelectWidgetOptions.tsx';
export interface IAdvancedSingleSelectTableDataProps
  extends IWidgetComponentProps<IAdvancedSingleSelectWidget> {
  /** Props del widget già risolte (colonne, opzioni, configurazione). */
  widgetOptions: IWidgetParsedProps;
  /** Opzione attualmente selezionata (o null se nessuna). */
  objectFieldValue: IWidgetAdvancedOptionValue | null;
  /** Callback invocata alla selezione di un'opzione. */
  onChange: (value: IWidgetAdvancedOptionValue | null) => void;
}

/**
 * Componente per la tabella di selezione singola avanzata
 */
export function AdvancedSingleSelectTableData(
  props: IAdvancedSingleSelectTableDataProps,
) {
  const { widgetOptions, widget, objectFieldValue, onChange } = props;
  const isMobile = useBreakpoint(down('md'));

  const dataSource = useAdvancedSingleSelectWidgetOptions({
    widget,
    name: widgetOptions.name,
    preview: widgetOptions.preview,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const rawColumns = useMemo(
    () => getAdvancedTableDataColumns(widget),
    [widget],
  );
  const { setSearchValue, filterData } = useAdvancedSelectSearch(rawColumns);

  const filteredDataSource = useMemo(
    () => filterData(dataSource),
    [filterData, dataSource],
  );

  const columns = useMemo(() => {
    return (
      rawColumns?.map(
        col =>
          ({
            title: col.label,
            dataIndex: ['values', col.id],
            key: col.id,
            // Non attiviamo sulle colonne lo scrooll automatico,
            // per evitare che un solo testo lungo faccia scrollare la tabella.
            // Lasciamo che la tabella cerchi di adattarsi alla pagina,
            // se troppo grande allora si attiva lo scroll sul container,
            // columnFlags: ['enableScroll'],
            render: getAdvancedSelectColumnRenderByDatatype(col.type),
            sorter: (
              a: IRuntimeWidgetAdvancedOption,
              b: IRuntimeWidgetAdvancedOption,
            ) =>
              sortColumnValues(
                a.values?.[col.id],
                b.values?.[col.id],
                col.type,
              ),
          }) as ColumnType<IRuntimeWidgetAdvancedOption>,
      ) ?? []
    );
  }, [rawColumns]);

  const selectedRowKeys = useMemo(() => {
    return objectFieldValue
      ? [getUniqueSelectedOptionValue(widget, objectFieldValue)]
      : [];
  }, [objectFieldValue, widget]);

  return (
    <>
      <AdvancedSelectTableToolbar>
        <AdvancedSelectSearchInput
          onChange={value => {
            setSearchValue(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
        />
      </AdvancedSelectTableToolbar>
      <TableData
        removeShadow
        scrollableHeight={
          isMobile ? undefined : ADVANCED_SELECT_TABLE_SCROLLABLE_HEIGHT
        }
        dataSource={filteredDataSource}
        size="small"
        style={{ marginTop: '6px' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          pageSizeOptions: [5, 10, 20, 50],
        }}
        onChange={paginationConfig => {
          setPagination({
            current: paginationConfig.current ?? 1,
            pageSize: paginationConfig.pageSize ?? 10,
          });
        }}
        columns={columns}
        darkTitleBar={false}
        rowSelection={{
          fixed: 'left',
          columnWidth: 48,
          type: 'radio',
          onChange: (_, selectedRows) => {
            const nextValue =
              selectedRows.length > 0 ? (selectedRows[0].values ?? {}) : null;
            onChange(nextValue);
          },
          selectedRowKeys,
          getCheckboxProps: row => ({
            disabled: row.disabled === true,
          }),
        }}
      />
    </>
  );
}
